import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useEffect, useState } from "react";

import { Container, Card, Row, Col, Form, Button, Spinner, Alert, Badge, Table } from "react-bootstrap";
import { FaDownload, FaFileArchive, FaFilePdf } from "react-icons/fa";
import swal from "sweetalert2";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { getFirestore, collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { exportarToPDF } from "../../functions/exportarToPDF";

const db = getFirestore(firebaseApp);
const LEGJO_CONCURRENCY = 8;
const DOWNLOAD_CONCURRENCY = 6;
const FICHA_DOCUMENTO_ID = "0";

const mapWithConcurrency = async (items, limit, asyncMapper) => {
  const safeLimit = Math.max(1, limit || 1);
  const results = [];
  for (let i = 0; i < items.length; i += safeLimit) {
    const chunk = items.slice(i, i + safeLimit);
    const chunkResults = await Promise.all(chunk.map(asyncMapper));
    results.push(...chunkResults);
  }
  return results;
};

const normalizeDownloadUrl = (rawUrl) => {
  if (!rawUrl) return rawUrl;
  try {
    const parsed = new URL(rawUrl);
    const isStorageGoogleApis = parsed.hostname === "storage.googleapis.com";
    if (!isStorageGoogleApis) return rawUrl;

    const pathname = parsed.pathname.replace(/^\/+/, "");
    const [bucket, ...objectPathParts] = pathname.split("/");
    if (!bucket || objectPathParts.length === 0) return rawUrl;

    const objectPath = objectPathParts.join("/");
    const normalized = new URL(`https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(objectPath)}`);
    normalized.searchParams.set("alt", "media");
    return normalized.toString();
  } catch (e) {
    return rawUrl;
  }
};

function DescargarDocumentos({ usuario }) {
  const [documentosMaestros, setDocumentosMaestros] = useState([]);
  const [documentoId, setDocumentoId] = useState("");
  const [mes, setMes] = useState(String(new Date().getMonth() + 1));
  const [ano, setAno] = useState(String(new Date().getFullYear()));
  const [loadingAction, setLoadingAction] = useState(null); // "zip" | "reporte" | null
  const [error, setError] = useState(null);
  const [registrosEncontrados, setRegistrosEncontrados] = useState([]);
  const [progresoBusqueda, setProgresoBusqueda] = useState({ procesados: 0, total: 0 });

  useEffect(() => {
    const cargarMaestros = async () => {
      try {
        setError(null);
        const rucEmpresa = usuario?.ruc || "20538856674";
        const docsRef = collection(db, `onboarding_lista/${rucEmpresa}/documentos`);
        const docsSnap = await getDocs(docsRef);
        const lista = [];
        docsSnap.forEach((docSnap) => {
          lista.push({ id: docSnap.id, ...docSnap.data() });
        });
        const existeFicha = lista.some((docItem) => String(docItem.id) === FICHA_DOCUMENTO_ID);
        if (!existeFicha) {
          lista.unshift({
            id: FICHA_DOCUMENTO_ID,
            nombre: "Ficha de datos personales",
            tipo: "Ficha"
          });
        }
        setDocumentosMaestros(lista);
      } catch (e) {
        console.error(e);
        setError("No se pudo cargar la lista maestra de documentos.");
      }
    };
    cargarMaestros();
  }, [usuario?.ruc]);

  const obtenerUsuariosEmpresa = async (rucActual) => {
    const usersRef = collection(db, "users");
    let usersSnap = await getDocs(query(usersRef, where("rucEmpresa", "==", rucActual)));
    if (usersSnap.empty) {
      usersSnap = await getDocs(query(usersRef, where("ruc", "==", rucActual)));
    }

    const usuarios = [];
    usersSnap.forEach((userDoc) => {
      const data = userDoc.data();
      if (data.activo === false || data.activo === "false") return;
      usuarios.push({
        dni: data.dni || userDoc.id,
        colaborador: `${data.apellidos || ""} ${data.name || ""}`.trim() || userDoc.id,
        email: data.email || ""
      });
    });
    return usuarios;
  };

  const obtenerRegistrosDocumento = async () => {
    if (!documentoId) {
      swal.fire("Error", "Seleccione un documento.", "error");
      return null;
    }

    const rucActual = (usuario?.ruc || "").toString().trim();
    if (!rucActual) {
      swal.fire("Error", "No se encontró el RUC de la empresa.", "error");
      return null;
    }

    const usuarios = await obtenerUsuariosEmpresa(rucActual);
    if (usuarios.length === 0) return [];
    setRegistrosEncontrados([]);
    setProgresoBusqueda({ procesados: 0, total: usuarios.length });

    const mesObjetivo = parseInt(mes, 10);
    const anoObjetivo = parseInt(ano, 10);
    const objetivo = String(documentoId);
    const nombreDocumento = documentosMaestros.find((d) => String(d.id) === objetivo)?.nombre || "Documento";
    const registrosPorUsuario = await mapWithConcurrency(usuarios, LEGJO_CONCURRENCY, async (trabajador) => {
      const registros = [];

      if (objetivo === FICHA_DOCUMENTO_ID) {
        const fichaLegajoRef = doc(db, `onboarding/${trabajador.dni}/legajo`, "ficha_datos_personales");
        const fichaLegajoSnap = await getDoc(fichaLegajoRef);
        if (fichaLegajoSnap.exists()) {
          const data = fichaLegajoSnap.data() || {};
          const rawFecha = data.fechaFirma || data.fechaDoc || data.fechaVista || data.fechaEnvio || data.fecha;

          let fecha = null;
          if (rawFecha && typeof rawFecha.toDate === "function") {
            fecha = rawFecha.toDate();
          } else if (rawFecha?.seconds) {
            fecha = new Date(rawFecha.seconds * 1000);
          } else if (rawFecha instanceof Date) {
            fecha = rawFecha;
          } else if (typeof rawFecha === "string") {
            const parsed = new Date(rawFecha);
            if (!Number.isNaN(parsed.getTime())) fecha = parsed;
          }

          if (fecha) {
            const mesRegistro = data.mes ? parseInt(data.mes, 10) : fecha.getMonth() + 1;
            const anoRegistro = data.ano ? parseInt(data.ano, 10) : fecha.getFullYear();
            if (mesRegistro === mesObjetivo && anoRegistro === anoObjetivo) {
              registros.push({
                dni: trabajador.dni,
                colaborador: trabajador.colaborador,
                email: trabajador.email,
                tipo: data.nombre || data.tipo || data.titulo || "Ficha de datos personales",
                tipoId: FICHA_DOCUMENTO_ID,
                periodo: String(mes).padStart(2, "0"),
                ano: String(ano),
                fechaDoc: data.fechaDoc || fecha,
                ruc: data.ruc || rucActual,
                contador: data.contador || (data.fechaVista ? 1 : 0),
                plataforma: data.plataforma || "web",
                fechaFirma: data.fechaFirma || null,
                fechaVista: data.fechaVista || null,
                link: data.link || null,
                linkFirmado: data.linkFirmado || null
              });
            }
          }
        }
      } else {
      const legajoRef = collection(db, `onboarding/${trabajador.dni}/legajo`);
      const legajoSnap = await getDocs(legajoRef);

      legajoSnap.forEach((legajoDoc) => {
        const data = legajoDoc.data();
        const idDoc = String(data.documentId || data.idDocument || data.id || "");
        if (idDoc !== objetivo) return;

        const rawFecha = data.fechaDoc || data.fechaEnvio || data.fechaFirma || data.fechaVista || data.fecha;
        if (!rawFecha) return;

        let fecha;
        if (typeof rawFecha.toDate === "function") {
          fecha = rawFecha.toDate();
        } else if (rawFecha?.seconds) {
          fecha = new Date(rawFecha.seconds * 1000);
        } else if (rawFecha instanceof Date) {
          fecha = rawFecha;
        } else {
          return;
        }

        const mesRegistro = data.mes ? parseInt(data.mes, 10) : fecha.getMonth() + 1;
        const anoRegistro = data.ano ? parseInt(data.ano, 10) : fecha.getFullYear();
        if (mesRegistro !== mesObjetivo || anoRegistro !== anoObjetivo) return;

        registros.push({
          dni: trabajador.dni,
          colaborador: trabajador.colaborador,
          email: trabajador.email,
          tipo: data.nombre || data.tipo || data.titulo || nombreDocumento,
          tipoId: idDoc,
          periodo: String(mes).padStart(2, "0"),
          ano: String(ano),
          fechaDoc: data.fechaDoc || null,
          ruc: data.ruc || rucActual,
          contador: data.contador || (data.fechaVista ? 1 : 0),
          plataforma: data.plataforma || "web",
          fechaFirma: data.fechaFirma || (data.linkFirmado ? data.fechaDoc : null),
          fechaVista: data.fechaVista || null,
          link: data.link || null,
          linkFirmado: data.linkFirmado || null
        });
      });
      }
      if (registros.length > 0) {
        const resumenUsuario = registros.reduce((acc, reg) => {
          if (!acc) return reg;
          const fechaAcc = acc.fechaFirma || acc.fechaVista || acc.fechaDoc;
          const fechaReg = reg.fechaFirma || reg.fechaVista || reg.fechaDoc;
          const tAcc =
            typeof fechaAcc?.toDate === "function"
              ? fechaAcc.toDate().getTime()
              : fechaAcc?.seconds
                ? fechaAcc.seconds * 1000
                : fechaAcc instanceof Date
                  ? fechaAcc.getTime()
                  : 0;
          const tReg =
            typeof fechaReg?.toDate === "function"
              ? fechaReg.toDate().getTime()
              : fechaReg?.seconds
                ? fechaReg.seconds * 1000
                : fechaReg instanceof Date
                  ? fechaReg.getTime()
                  : 0;
          return tReg > tAcc ? reg : acc;
        }, null);

        setRegistrosEncontrados((prev) => {
          const sinUsuario = prev.filter((item) => item.dni !== trabajador.dni);
          return [...sinUsuario, resumenUsuario];
        });
      }
      setProgresoBusqueda((prev) => ({ ...prev, procesados: Math.min(prev.procesados + 1, prev.total) }));
      return registros;
    });

    return registrosPorUsuario.flat();
  };

  const descargarZip = async () => {
    try {
      setLoadingAction("zip");
      setError(null);

      const registros = await obtenerRegistrosDocumento();
      if (!registros) return;
      if (registros.length === 0) {
        swal.fire("Sin datos", `No se encontraron registros para ${mes}/${ano}.`, "info");
        return;
      }

      const objetivo = String(documentoId);
      const zip = new JSZip();
      const folder = zip.folder(`DOC_${objetivo}_${ano}${String(mes).padStart(2, "0")}`);
      let agregados = 0;

      const sanitize = (text = "") =>
        text
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-zA-Z0-9_-]/g, "_")
          .replace(/_+/g, "_")
          .replace(/^_|_$/g, "");

      const extFrom = (url, contentType) => {
        if (contentType?.includes("pdf")) return "pdf";
        if (contentType?.includes("png")) return "png";
        if (contentType?.includes("jpeg") || contentType?.includes("jpg")) return "jpg";
        if (contentType?.includes("webp")) return "webp";
        try {
          const path = new URL(url).pathname;
          const file = decodeURIComponent(path.split("/").pop() || "");
          if (file.includes(".")) return file.split(".").pop().toLowerCase();
        } catch (e) {
          console.warn("No se pudo inferir extensión", e);
        }
        return "bin";
      };

      const descargas = await mapWithConcurrency(
        registros.map((item, index) => ({ item, index })),
        DOWNLOAD_CONCURRENCY,
        async ({ item, index }) => {
          const rawLink = item.linkFirmado || item.link;
          const link = normalizeDownloadUrl(rawLink);
          if (!link) return null;

          try {
            const response = await fetch(link);
            if (!response.ok) return null;
            const blob = await response.blob();
            const ext = extFrom(link, blob.type);
            const name = `${String(index + 1).padStart(4, "0")}_${item.dni}_${sanitize(item.colaborador)}.${ext}`;
            return { name, blob };
          } catch (fetchError) {
            console.warn("Error descargando archivo:", item.dni, fetchError);
            return null;
          }
        }
      );

      descargas.forEach((descarga) => {
        if (!descarga) return;
        folder.file(descarga.name, descarga.blob);
        agregados += 1;
      });

      if (agregados === 0) {
        swal.fire("Sin archivos", "Se encontraron registros, pero no se pudo descargar archivos válidos.", "warning");
        return;
      }

      const rucActual = (usuario?.ruc || "").toString().trim();
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `Onboarding_Doc_${objetivo}_${ano}${String(mes).padStart(2, "0")}_${rucActual}.zip`);
      swal.fire("Completado", `ZIP generado con ${agregados} archivos.`, "success");
    } catch (e) {
      console.error(e);
      setError("Ocurrió un error al generar el ZIP.");
      swal.fire("Error", "No se pudo generar el ZIP de documentos.", "error");
    } finally {
      setLoadingAction(null);
      setProgresoBusqueda((prev) => ({ ...prev, procesados: prev.total }));
    }
  };

  const descargarReporte = async () => {
    try {
      setLoadingAction("reporte");
      setError(null);

      const registros = await obtenerRegistrosDocumento();
      if (!registros) return;
      if (registros.length === 0) {
        swal.fire("Sin datos", `No se encontraron envíos para ${mes}/${ano}.`, "info");
        return;
      }

      const limpiarTextoPDF = (value) =>
        String(value ?? "")
          .replace(/[\r\n\t]+/g, " ")
          .replace(/[\u0000-\u001F\u007F]/g, " ")
          .replace(/\s+/g, " ")
          .trim();

      const registrosLimpios = registros.map((item) => ({
        ...item,
        dni: limpiarTextoPDF(item.dni),
        colaborador: limpiarTextoPDF(item.colaborador),
        tipo: limpiarTextoPDF(item.tipo),
        periodo: limpiarTextoPDF(item.periodo),
        ano: limpiarTextoPDF(item.ano),
        email: limpiarTextoPDF(item.email),
        plataforma: limpiarTextoPDF(item.plataforma),
        ruc: limpiarTextoPDF(item.ruc),
      }));

      const rucActual = (usuario?.ruc || "").toString().trim();
      const pdfBytes = await exportarToPDF(registrosLimpios, rucActual);
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Reporte_Envio_Doc_${documentoId}_${ano}${String(mes).padStart(2, "0")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setError("Ocurrió un error al generar el reporte.");
      swal.fire("Error", "No se pudo generar el reporte de envío.", "error");
    } finally {
      setLoadingAction(null);
      setProgresoBusqueda((prev) => ({ ...prev, procesados: prev.total }));
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return "-";
    if (typeof fecha?.toDate === "function") return fecha.toDate().toLocaleString("es-PE", { timeZone: "America/Lima" });
    if (fecha?.seconds) return new Date(fecha.seconds * 1000).toLocaleString("es-PE", { timeZone: "America/Lima" });
    if (fecha instanceof Date) return fecha.toLocaleString("es-PE", { timeZone: "America/Lima" });
    return "-";
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        <Card className="shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}>
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderBottom: "3px solid #f39c12",
            }}
          >
            <div style={{position:"relative",display:"flex",alignItems:"center",justifyContent:"center"}} className="w-100">

              <div className="text-center">
                <h2 className="fw-bold mb-1">Descargar Documentos Onboarding</h2>
                <p className="mb-0 opacity-75">Descarga masiva por tipo de documento y reporte de envío</p>
              </div>
              <div style={{position:"absolute",right:"20px"}}>
                <BotonManualHeader usuario={usuario} />
              </div>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {error && <Alert variant="warning" className="border-0 shadow-sm mb-4">{error}</Alert>}

            <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body>
                <h6 className="fw-bold text-dark mb-2">¿Qué puede hacer en este módulo?</h6>
                <p className="text-muted mb-2">
                  Este componente permite gestionar descargas masivas de documentos de onboarding a nivel empresa, sin necesidad de buscar colaboradores por DNI.
                </p>
                <ul className="text-muted mb-2">
                  <li><strong>Descargar ZIP por tipo de documento:</strong> consolida en un solo archivo todos los registros encontrados por documento, mes y año.</li>
                  <li><strong>Descargar reporte de envío (PDF):</strong> genera un reporte ejecutivo con el detalle de envíos identificados en el periodo.</li>
                  <li><strong>Seguimiento en tiempo real:</strong> muestra una tabla con los colaboradores que sí tienen el documento seleccionado y su estado de disponibilidad.</li>
                </ul>
                <p className="text-muted mb-0">
                  Este flujo está orientado a control operativo, auditoría documentaria y trazabilidad para áreas de RR.HH. y cumplimiento.
                </p>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px", backgroundColor: "#f8f9ff", border: "1px solid #e0e7ff" }}>
              <Card.Body className="py-3">
                <Row className="g-3 align-items-end">
                  <Col md={6}>
                    <Form.Label className="fw-semibold">Tipo de documento</Form.Label>
                    <Form.Select value={documentoId} onChange={(e) => setDocumentoId(e.target.value)} disabled={!!loadingAction} className="rounded-3 border-primary">
                      <option value="">-- Seleccionar documento --</option>
                      {documentosMaestros.map((doc) => {
                        const nombre = doc.nombre || doc.titulo || doc.title || doc.tipo || "Sin nombre";
                        return (
                          <option key={doc.id} value={doc.id}>
                            {doc.id} - {nombre}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fw-semibold">Mes</Form.Label>
                    <Form.Select value={mes} onChange={(e) => setMes(e.target.value)} disabled={!!loadingAction} className="rounded-3">
                      <option value="1">Enero</option>
                      <option value="2">Febrero</option>
                      <option value="3">Marzo</option>
                      <option value="4">Abril</option>
                      <option value="5">Mayo</option>
                      <option value="6">Junio</option>
                      <option value="7">Julio</option>
                      <option value="8">Agosto</option>
                      <option value="9">Septiembre</option>
                      <option value="10">Octubre</option>
                      <option value="11">Noviembre</option>
                      <option value="12">Diciembre</option>
                    </Form.Select>
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fw-semibold">Año</Form.Label>
                    <Form.Select value={ano} onChange={(e) => setAno(e.target.value)} disabled={!!loadingAction} className="rounded-3">
                      <option value="2023">2023</option>
                      <option value="2024">2024</option>
                      <option value="2025">2025</option>
                      <option value="2026">2026</option>
                    </Form.Select>
                  </Col>
                </Row>
                <Row className="g-3 mt-1">
                  <Col md={6}>
                    <Button
                      variant="success"
                      onClick={descargarZip}
                      disabled={!!loadingAction || !documentoId}
                      className="fw-bold w-100 py-2"
                    >
                      {loadingAction === "zip" ? <Spinner size="sm" animation="border" className="me-2" /> : <FaFileArchive className="me-2" />}
                      Descargar ZIP
                    </Button>
                  </Col>
                  <Col md={6}>
                    <Button
                      variant="danger"
                      onClick={descargarReporte}
                      disabled={!!loadingAction || !documentoId}
                      className="fw-bold w-100 py-2"
                    >
                      {loadingAction === "reporte" ? <Spinner size="sm" animation="border" className="me-2" /> : <FaFilePdf className="me-2" />}
                      Descargar Reporte
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
              <Card.Body>
                <div className="text-muted small">
                  <FaDownload className="me-1" />
                  No requiere DNI: procesa a toda la empresa usando el RUC actual.
                </div>
                {(!!loadingAction || registrosEncontrados.length > 0) && (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h6 className="mb-1 fw-bold text-dark">Registros encontrados</h6>
                        <small className="text-muted">
                          {registrosEncontrados.length} usuario(s) con el documento seleccionado
                        </small>
                      </div>
                      <small className="text-muted fw-semibold">
                        {progresoBusqueda.procesados}/{progresoBusqueda.total} colaboradores procesados
                      </small>
                    </div>
                    <div
                      style={{
                        maxHeight: "360px",
                        overflowY: "auto",
                        border: "1px solid #e9ecef",
                        borderRadius: "12px",
                        background: "#fff",
                      }}
                    >
                      <Table hover responsive className="mb-0 align-middle">
                        <thead style={{ position: "sticky", top: 0, background: "#f4f7fb", zIndex: 1 }}>
                          <tr>
                            <th className="text-center text-secondary fw-semibold" style={{ width: "70px" }}>#</th>
                            <th>DNI</th>
                            <th>Colaborador</th>
                            <th>Documento</th>
                            <th>Fecha</th>
                            <th className="text-center">Estado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {registrosEncontrados.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="text-center text-muted py-4">
                                {!!loadingAction ? "Buscando registros..." : "Sin registros para los filtros seleccionados."}
                              </td>
                            </tr>
                          ) : (
                            registrosEncontrados.map((r, idx) => (
                              <tr key={`${r.dni}-${idx}`}>
                                <td className="text-center fw-bold text-primary">{idx + 1}</td>
                                <td className="fw-semibold">{r.dni}</td>
                                <td>{r.colaborador}</td>
                                <td>
                                  <span className="fw-semibold">{r.tipoId}</span>
                                  <span className="text-muted"> - {r.tipo}</span>
                                </td>
                                <td>{formatearFecha(r.fechaFirma || r.fechaVista || r.fechaDoc)}</td>
                                <td className="text-center">
                                  {r.linkFirmado || r.link ? (
                                    <Badge bg="success" pill>Disponible</Badge>
                                  ) : (
                                    <Badge bg="secondary" pill>Sin enlace</Badge>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default DescargarDocumentos;


